import { z } from 'zod';
import { UserRole } from './car-enums';
import { IdentityProviderSchema, KycStatusSchema } from './kyc';
import { OAuthIdentitySchema } from './mobile-auth';
import { PricingPlanTierSchema } from './pricing-rules';
/**
 * Minimum local password length. ONE number for the API (users.service.ts
 * changePassword, password-reset confirm) and the web forms, so the browser
 * can never accept what the server refuses.
 */
export const PASSWORD_MIN_LENGTH = 10;
/**
 * Version of the WhatsApp opt-in text the client agreed to (Phase 12). Bump
 * when the legal wording changes; the stored `consentVersion` says WHICH text
 * a given consent covered. See docs/adr/ADR-whatsapp-channel.md.
 */
export const WHATSAPP_CONSENT_VERSION = '2026-09';
/** `+` followed by 7-15 digits, no leading zero — the E.164 envelope. */
export const E164_PHONE_REGEX = /^\+[1-9]\d{6,14}$/;
/**
 * WhatsApp link on a user (Phase 12, additive — `users.whatsapp`).
 *
 * `phoneE164` is the ONLY key the channel addresses; it is unique among
 * ACTIVE links (a partial unique index on `optedOutAt: null` in
 * index-definitions.ts) so one number is linked to one account at a time.
 * `optedOutAt` set = the client said STOP, unlinked, or the number was linked
 * by another account: the channel skips them and the record of consent is
 * kept rather than deleted, because "they had consented until <date>" is the
 * audit trail a complaint is answered with. `detachedAt` marks the third case
 * (the number moved to another account), so a support reader can tell a
 * withdrawal from a takeover.
 */
export const UserWhatsAppSchema = z.object({
    phoneE164: z.string().regex(E164_PHONE_REGEX, 'phone must be E.164'),
    linkedAt: z.coerce.date(),
    consentAt: z.coerce.date(),
    consentVersion: z.string().min(1),
    optedOutAt: z.coerce.date().nullish(),
    detachedAt: z.coerce.date().nullish(),
});
/**
 * Delivery address (Phase — client-cabinet "Особисті дані" fix, additive).
 * The card has shown this as required-for-transport copy since it was built,
 * but there was no field or endpoint behind it — editing it was a dead end.
 * Minimal shape for what shipping a won car actually needs: country + city +
 * a single street/building line + postal code are load-bearing; `region`
 * (область/state) and `recipientName` (when the recipient differs from the
 * account holder) are genuinely optional. The FIELD is optional on the user
 * (an account exists fine without one) but once someone starts filling it in,
 * the sub-fields below are required — a half-filled address is not usable by
 * a carrier either.
 */
export const DeliveryAddressSchema = z.object({
    country: z.string().min(1),
    region: z.string().nullish(),
    city: z.string().min(1),
    /** Street name + building/apartment number as one line. */
    street: z.string().min(1),
    postalCode: z.string().min(1),
    /** Defaults to the account holder's name when absent. */
    recipientName: z.string().nullish(),
});
/**
 * Single-line rendering of a delivery address — «country, region, city,
 * street, postal code (recipient)». Absent or all-blank → `''`, so a caller can
 * treat it as "not provided". One formatter for the client profile and the
 * admin, so both print the same line.
 */
export function formatDeliveryAddress(address) {
    if (!address)
        return '';
    const parts = [address.country, address.region, address.city, address.street, address.postalCode].filter((part) => typeof part === 'string' && part.trim() !== '');
    if (parts.length === 0)
        return '';
    const recipient = address.recipientName?.trim() ? ` (${address.recipientName.trim()})` : '';
    return `${parts.join(', ')}${recipient}`;
}
/**
 * How staff should see a person: the real «First Last» first, then a stored
 * `name`, then the chosen `displayName` (often a handle such as
 * «atretiak.pro»), then the email — `''` when none is set, so callers pick
 * their own last resort (usually the id). `displayName` used to win, which
 * hid a client's real name behind their handle across the admin.
 */
export function personDisplayName(user) {
    if (!user)
        return '';
    const clean = (value) => (typeof value === 'string' ? value.trim() : '');
    const full = [clean(user.firstName), clean(user.lastName)].filter(Boolean).join(' ');
    return full || clean(user.name) || clean(user.displayName) || clean(user.email);
}
/**
 * User contract (`users` collection).
 *
 * role is an ARRAY of roles (web semantics: `role.includes('admin')`), now
 * extended to admin | staff | user | dealer. `dealer` is granted starting
 * phase 14 — the contract admits it today so no consumer breaks then.
 */
export const UserSchema = z
    .object({
    _id: z.unknown().nullish(),
    email: z.string(),
    name: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    displayName: z.string().nullish(),
    /** ISO date string YYYY-MM-DD (web KYC forms). */
    dateOfBirth: z.string().nullish(),
    phone: z.string().nullish(),
    role: z.array(z.nativeEnum(UserRole)).nullish(),
    photoURL: z.string().nullish(),
    password: z.string().nullish(), // hash; never leaves the API layer
    verified: z.boolean().nullish(),
    createdAt: z.coerce.date().nullish(),
    /** Max simultaneous active bids (default 1, admin-adjustable). */
    bidLimit: z.number().int().nullish(),
    /**
     * The client's DEPOSIT in CHF (Phase 10 semantics — the field name is kept
     * for wire parity, never renamed in the DB). It is NOT a bid ceiling by
     * itself: the ceiling on the client's OPEN exposure (every active +
     * relayed bid summed) is `biddingPower × plan.biddingPowerCoef`
     * (retail/dealer ×10, dealer_pro ×15), further capped by
     * `exposureLimitChf` — see `resolveExposureCeiling` in exposure.ts.
     * Default 0; set by an admin (PATCH /api/admin/users/:id/exposure) until
     * phase 13 automates it through the PSP.
     */
    biddingPower: z.number().nullish(),
    /**
     * Admin-set absolute cap on open exposure, CHF (Phase 10). `null`/absent =
     * no admin cap. For an exempt client (see `exposureExempt`) this is the
     * ONLY limit that applies.
     */
    exposureLimitChf: z.number().nullable().optional(),
    /**
     * Verified top client: not bound by the deposit-derived ceiling, only by
     * `exposureLimitChf` (Phase 10, owner decision). Set by an admin, never by
     * the client. A `dealer_pro` client with `verified: true` is exempt
     * implicitly — see `isExposureExempt`.
     */
    exposureExempt: z.boolean().nullish(),
    /**
     * KYC verification status (Phase 13, additive). Absent = 'none' (see
     * `effectiveKycStatus`). Driven by the Sumsub webhook when
     * `KYC_PROVIDER=sumsub`, and by an admin document approval in the default
     * `manual` mode.
     */
    kycStatus: KycStatusSchema.nullish(),
    /**
     * Auth.js's own field (Phase 9). A Date = the address was proven; `null`
     * = a row the backfill classified as unproven; absent = never classified.
     * RUNG 1 of the verification ladder (see `deriveVerificationLevel`).
     */
    emailVerified: z.coerce.date().nullish(),
    /**
     * Provider identities linked by the native (mobile) OAuth sign-in
     * (mobile-app-expo phase 1, additive). Absent = none linked.
     */
    oauthIdentities: z.array(OAuthIdentitySchema).nullish(),
    /**
     * When the client confirmed an OTP sent to `phone` (Phase 14, additive).
     * RUNG 2. Cleared whenever the number changes — a verified timestamp must
     * never outlive the number it verified.
     */
    phoneVerifiedAt: z.coerce.date().nullish(),
    /**
     * When identity was asserted (Phase 14, additive). RUNG 3. Written by the
     * Sumsub webhook on `GREEN`, or by an admin document approval in manual
     * mode; unset again if the identity is later rejected.
     */
    identityVerifiedAt: z.coerce.date().nullish(),
    /** Who asserted it. Meaningless without `identityVerifiedAt`. */
    identityProvider: IdentityProviderSchema.nullish(),
    /** Sumsub applicant id for this user (`KYC_PROVIDER=sumsub` only). */
    sumsubApplicantId: z.string().nullish(),
    /**
     * Pricing plan tier (Phase 14 B2B, additive). Absent = 'retail'. Drives the
     * deal success-fee resolution, fixed at win time (see pricing-rules.ts).
     *
     * With volume tiers published (`tierRules` on the active rule set) it is
     * EARNED: recomputed from paid cars in the rolling window by
     * `resolveVolumeTier` (volume-tier.ts); a dealer-application approval does
     * not touch it. With `tierRules: null` the legacy behaviour stands: approval
     * sets it to 'dealer'.
     */
    plan: PricingPlanTierSchema.nullish(),
    /**
     * End of the running volume-tier hold (additive): the tier in `plan` is no
     * longer earned and is kept until this instant, then drops one step. Absent /
     * null = no hold running.
     */
    planHoldUntil: z.coerce.date().nullish(),
    /** When `plan` was last evaluated against the volume-tier rules (additive). */
    planEvaluatedAt: z.coerce.date().nullish(),
    /**
     * This user's own referral code (Phase 14, additive). Shared with invitees;
     * a new signup carrying it in `referredBy` credits this user on their first
     * closed deal.
     */
    referralCode: z.string().nullish(),
    /** The referral code this user signed up under (points at a referrer). */
    referredBy: z.string().nullish(),
    /**
     * dealer_pro subscription paid-through date (Phase 14). When it lapses the
     * (payments-gated) downgrade cron drops the user back to the `dealer` tier.
     * Purely informational while PAYMENTS_ENABLED=false.
     *
     * @deprecated the tariff no longer has a subscription — `dealer_pro` is earned
     * by volume (see `plan`). Kept so stored documents still parse.
     */
    subscriptionValidUntil: z.coerce.date().nullish(),
    /**
     * WhatsApp link (Phase 12, additive). Absent = never linked. Present with
     * `optedOutAt` = linked once, since withdrawn — never messaged again until
     * a fresh OTP link re-stamps consent.
     */
    whatsapp: UserWhatsAppSchema.nullish(),
    /** See `DeliveryAddressSchema` above. Absent = never provided. */
    deliveryAddress: DeliveryAddressSchema.nullish(),
})
    // KYC/verification/DIA fields and refreshTokens stay unmodeled (passthrough)
    // until their owning phase promotes them into the contract.
    .passthrough();
//# sourceMappingURL=user.js.map